import React, { useEffect, useState } from 'react';
import { Drawer, Descriptions, Tag, Tabs, Table, Select, Button, Form, message, Spin } from 'antd';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import type { WaveInstanceDTO, WaveInstanceOrderDTO, QueryWaveOrdersRequest } from '../../../../types/wave/waveInstance';
import type { Paging } from '../../../../types/common';
import * as waveInstanceService from '../../../../services/wave/waveInstanceService';
import {
  WaveStatusOptions,
} from '../../../../constants/wave';

interface WaveInstanceDetailProps {
  visible: boolean;
  waveNo: string;
  onClose: () => void;
}

const WaveInstanceDetail: React.FC<WaveInstanceDetailProps> = ({ visible, waveNo, onClose }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [detail, setDetail] = useState<WaveInstanceDTO | null>(null);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [ordersData, setOrdersData] = useState<WaveInstanceOrderDTO[]>([]);
  const [ordersPagination, setOrdersPagination] = useState<Paging>({ currentPage: 1, pageSize: 20, totalCount: 0 });
  const [allocationStatusFilter, setAllocationStatusFilter] = useState<string | undefined>(undefined);

  useEffect(() => {
    if (visible && waveNo) {
      loadDetail();
      loadOrders({ currentPage: 1, pageSize: 20 });
    }
  }, [visible, waveNo]);

  const loadDetail = async () => {
    setLoading(true);
    try {
      const response = await waveInstanceService.getWaveInstanceByNo(waveNo);
      if (response.success && response.data) {
        setDetail(response.data);
      } else {
        message.error(response.errorMessage || t('common.message.loadFailed'));
      }
    } catch (error) {
      message.error(t('common.message.loadFailed'));
    } finally {
      setLoading(false);
    }
  };

  const loadOrders = async (params: QueryWaveOrdersRequest) => {
    setOrdersLoading(true);
    try {
      const response = await waveInstanceService.getWaveOrders(waveNo, params);
      if (response.success && response.data) {
        setOrdersData(response.data.tableData);
        setOrdersPagination(response.data.paging);
      } else {
        message.error(response.errorMessage || t('common.message.loadFailed'));
      }
    } catch (error) {
      message.error(t('common.message.loadFailed'));
    } finally {
      setOrdersLoading(false);
    }
  };

  const handleOrdersPageChange = (page: number, pageSize: number) => {
    loadOrders({
      currentPage: page,
      pageSize,
      allocationStatus: allocationStatusFilter,
    });
  };



  // Format datetime
  const formatDateTime = (timestamp: number | null) => {
    return timestamp ? dayjs(timestamp).format('YYYY-MM-DD HH:mm:ss') : '-';
  };

  // Render status tag
  const renderStatusTag = (status: string, options: any[]) => {
    const option = options.find(opt => opt.value === status);
    if (!option) return status;
    return <Tag color={option.color}>{t(option.label)}</Tag>;
  };

  // Orders table columns
  const ordersColumns = [
    {
      title: t('wave.waveInstance.detail.orders.table.orderNo'),
      dataIndex: 'orderNo',
      key: 'orderNo',
      width: 160,
    },
    {
      title: t('wave.waveInstance.detail.orders.table.orderStatus'),
      dataIndex: 'orderStatus',
      key: 'orderStatus',
      width: 120,
      render: (status: string) => {
        if (!status) return '-';
        const colorMap: Record<string, string> = {
          CREATED: 'blue',
          IN_PROGRESS: 'orange',
          COMPLETED: 'green',
          CANCELLED: 'red',
        };
        return <Tag color={colorMap[status] || 'default'}>{status}</Tag>;
      },
    },
    {
      title: t('wave.waveInstance.detail.orders.table.taskCode'),
      dataIndex: 'taskCode',
      key: 'taskCode',
      width: 160,
      render: (text: string | null) => text || '-',
    },
    {
      title: t('wave.waveInstance.detail.orders.table.createTime'),
      dataIndex: 'gmtCreate',
      key: 'gmtCreate',
      width: 180,
      render: formatDateTime,
    },
  ];



  if (!detail) {
    return (
      <Drawer
        title={t('wave.waveInstance.detail.title')}
        width={960}
        open={visible}
        onClose={onClose}
      >
        <Spin spinning={loading} />
      </Drawer>
    );
  }

  return (
    <Drawer
      title={t('wave.waveInstance.detail.title')}
      width={960}
      open={visible}
      onClose={onClose}
      footer={
        <div style={{ textAlign: 'right' }}>
          <Button onClick={onClose}>{t('common.close')}</Button>
        </div>
      }
    >
      <Spin spinning={loading}>
        {/* Basic Information */}
        <Descriptions title={t('wave.waveInstance.detail.basicInfo.title')} column={3} bordered>
          <Descriptions.Item label={t('wave.waveInstance.detail.basicInfo.waveNo')}>
            {detail.waveNo}
          </Descriptions.Item>
          <Descriptions.Item label={t('wave.waveInstance.detail.basicInfo.waveStatus')}>
            {renderStatusTag(detail.waveStatus, WaveStatusOptions)}
          </Descriptions.Item>
          <Descriptions.Item label={t('wave.waveInstance.detail.basicInfo.wavePlanCode')}>
            {detail.wavePlanCode || '-'}
          </Descriptions.Item>
          <Descriptions.Item label={t('wave.waveInstance.detail.basicInfo.wavePlanName')} span={3}>
            {detail.wavePlanName || '-'}
          </Descriptions.Item>
          <Descriptions.Item label={t('wave.waveInstance.detail.basicInfo.successOrderCount')}>
            {detail.successOrderCount}
          </Descriptions.Item>
          <Descriptions.Item label={t('wave.waveInstance.detail.basicInfo.storeName')}>
            {detail.storeName || detail.storeCode || '-'}
          </Descriptions.Item>
          <Descriptions.Item label={t('wave.waveInstance.detail.basicInfo.createTime')}>
            {formatDateTime(detail.gmtCreate)}
          </Descriptions.Item>
        </Descriptions>

        {/* Orders Tab */}
        <Tabs
          style={{ marginTop: 24 }}
          items={[
            {
              key: 'orders',
              label: t('wave.waveInstance.detail.tabs.orders'),
              children: (
                <div>
                  {/* Orders Table */}
                  <Table
                    rowKey="orderNo"
                    columns={ordersColumns}
                    dataSource={ordersData}
                    loading={ordersLoading}
                    pagination={{
                      current: ordersPagination.currentPage,
                      pageSize: ordersPagination.pageSize,
                      total: ordersPagination.totalCount,
                      showSizeChanger: true,
                      showTotal: (total) => t('common.pagination.total', { total }),
                      onChange: handleOrdersPageChange,
                    }}
                  />
                </div>
              ),
            },
          ]}
        />
      </Spin>
    </Drawer>
  );
};

export default WaveInstanceDetail;
